import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Section, Box, Flex, Text, Button, IconButton, TextField, Select } from '@radix-ui/themes';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';

import PageTitle from 'components/layout/PageTitle';
import Pagination from 'components/table/Pagination';
import { SelectBox } from 'components/form';

import { instance } from 'api/axios.instance';
import { localList, regions } from 'app/constants';
import { selectType, selectRegion } from 'store/slices/users';

import thumbnailPlaceholder from 'assets/images/common/thumbnail-placeholder.svg';

import 'components/table/sortByLocal.scss';
import 'components/table/tableWrapper.scss';
import './localIssue.scss';
import { parseIssueYear, formatIssueYear, getIssueYearOptions, withIssueYear } from './localIssueYear';

// 지역 이슈 파인더 > 목록 (이미지 카드 형태)
export default function LocalIssueFinder() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const type = useSelector(selectType);
  const userRegion = useSelector(selectRegion);
  const canSelectRegion = type === 'master' || type === 'admin';
  const listRegion = canSelectRegion ? searchParams.get('region') || '' : (regions[userRegion] ?? userRegion ?? '');

  const [fetchData, setFetchData] = useState({ list: [], pagination: {}, years: [] });
  const [errorMessage, setErrorMessage] = useState('');
  const requestSequence = useRef(0);
  const selectedYear = parseIssueYear(searchParams.get('year'));
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(searchParams.get('search') || '');
  const [selectedSearchType, setSelectedSearchType] = useState(searchParams.get('searchType') || '제목');

  const searchTypeMap = { 제목: 'title', 작성자: 'author' };
  const apiBase = process.env.REACT_APP_API_BASE;

  const fetchList = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await instance('/local-issue/list', {
        method: 'POST',
        data: {
          pageNo: Number(searchParams.get('page')) || 1,
          pageSize: 9, // 페이지 별 노출 개수 (카드 3열 기준)
          searchType: searchTypeMap[searchParams.get('searchType')] || '',
          search: searchParams.get('search') || '',
          // 기관회원은 URL의 지역 필터와 무관하게 자기 지역만 조회한다.
          region: listRegion,
          ...(selectedYear !== undefined ? { year: selectedYear } : {})
        }
      });
      if (requestId !== requestSequence.current) return;
      setFetchData({
        pagination: response.data.data.pagination,
        list: response.data.data.issues || [],
        years: response.data.data.years || []
      });
    } catch {
      if (requestId !== requestSequence.current) return;
      setFetchData(previous => ({ ...previous, pagination: {}, list: [] }));
      setErrorMessage('지역 이슈 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [searchParams, listRegion, selectedYear]);

  useEffect(() => {
    fetchList();
    return () => {
      requestSequence.current += 1;
    };
  }, [fetchList]);

  const handleSearch = () => {
    searchParams.set('page', 1);
    searchParams.set('searchType', selectedSearchType);
    searchParams.set('search', searchText);
    setSearchParams(searchParams);
  };

  const handleRegion = code => {
    searchParams.set('page', 1);
    searchParams.set('region', code);
    searchParams.delete('searchType');
    searchParams.delete('search');
    setSearchText('');
    setSearchParams(searchParams);
  };

  const handlePageChange = pageNo => {
    const totalPages = fetchData.pagination?.totalPage;
    if (pageNo >= 1 && pageNo <= totalPages) {
      searchParams.set('page', pageNo);
      setSearchParams(searchParams);
    }
  };

  return (
    <Section>
      <PageTitle title="지역 이슈 파인더" />
      <Box className="box box-white">
        {/* 마스터/어드민만 전체 지역 필터를 사용한다. */}
        {canSelectRegion && (
          <Flex align="center" justify="between" mb="var(--space-10)" className="sort-by-local">
            {localList.map((el, i) => (
              <Button key={i} size="2" className={`body2 ${listRegion === el.code ? 'active' : ''}`} onClick={() => handleRegion(el.code)}>
                {el.name}
              </Button>
            ))}
          </Flex>
        )}

        {/* 총 개수 + 검색 */}
        <Flex justify="between" align="center">
          <Flex align="center">
            <Text as="p" color="gray">
              Total&nbsp;
              <Text color="sky">{loading ? '-' : fetchData.pagination?.totalCount?.toLocaleString() || '0'}</Text>
            </Text>
          </Flex>
          <Flex justify="end" align="center" gap="2" className="board-search-box">
            <Select.Root
              size="2"
              value={selectedYear === undefined ? 'all' : String(selectedYear)}
              onValueChange={value => setSearchParams(withIssueYear(searchParams, value))}
            >
              <Select.Trigger aria-label="분류 연도 필터" style={{ minWidth: '160px' }} />
              <Select.Content position="popper">
                <Select.Item value="all">전체 연도</Select.Item>
                {getIssueYearOptions(fetchData.years, selectedYear).map(year => (
                  <Select.Item key={year} value={String(year)}>
                    {year}년
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
            <Box flexBasis="160px" flexShrink="0">
              <SelectBox
                size="2"
                placeholder="선택해 주세요."
                value={selectedSearchType}
                options={['제목', '작성자']}
                onChange={value => setSelectedSearchType(value)}
              />
            </Box>
            <Box flexBasis="252px" flexShrink="1">
              <TextField.Root
                placeholder="내용을 입력해 주세요."
                color="gray"
                value={searchText}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleSearch();
                }}
                onChange={e => setSearchText(e.target.value)}
              />
            </Box>
            <IconButton className="rt-variant-text" onClick={handleSearch}>
              <MagnifyingGlassIcon width={36} height={36} />
            </IconButton>
          </Flex>
        </Flex>

        {/* 카드 그리드 */}
        {loading ? (
          <Box mt="4">
            <Text as="p" color="gray">
              목록을 불러오는 중입니다.
            </Text>
          </Box>
        ) : errorMessage ? (
          <Box mt="4">
            <Text as="p" role="alert" className="font-error">
              {errorMessage}
            </Text>
          </Box>
        ) : fetchData.pagination?.totalCount > 0 ? (
          <>
            <div className="issue-card-grid">
              {fetchData.list.map(issue => (
                <div key={issue.id} className="issue-card" onClick={() => navigate(`/localIssueFinder/detail/${issue.id}`)}>
                  <div className="issue-card-thumb">
                    <img
                      src={
                        issue.thumbnailUrl
                          ? issue.thumbnailUrl.startsWith('http')
                            ? issue.thumbnailUrl
                            : `${apiBase}${issue.thumbnailUrl}`
                          : thumbnailPlaceholder
                      }
                      alt={issue.title}
                      onError={e => {
                        e.currentTarget.src = thumbnailPlaceholder;
                      }}
                    />
                  </div>
                  <div className="issue-card-body">
                    <p className="issue-card-title sub-title1">{issue.title}</p>
                    <Flex align="center" gap="2">
                      {issue.region && (
                        <Text size="1" color="gray">
                          {localList.find(r => r.code === issue.region)?.name || issue.region}
                        </Text>
                      )}
                      <Text size="1" color="gray">
                        {formatIssueYear(issue.year)}
                      </Text>
                      <Text size="1" color="gray" ml="auto">
                        {issue.createDate?.slice(0, 10)}
                      </Text>
                    </Flex>
                  </div>
                </div>
              ))}
            </div>
            <Flex justify="center" mt="6">
              <Pagination totalPage={fetchData.pagination.totalPage} handlePageChange={handlePageChange} />
            </Flex>
          </>
        ) : (
          !loading && (
            <Box mt="var(--space-2)">
              <Flex direction="column" align="center" justify="center" gap="var(--space-5)" className="no-data">
                <img src={require('assets/images/icon/icon-nodata.svg').default} alt="데이터에러" />
                <Text as="p" color="gray" className="body1">
                  데이터가 없습니다.
                </Text>
              </Flex>
            </Box>
          )
        )}
      </Box>

      {/* 글쓰기 버튼 */}
      <Flex justify="end" mt="2">
        <Button className="btn-lg" onClick={() => navigate('/localIssueFinder/write')}>
          글쓰기
        </Button>
      </Flex>
    </Section>
  );
}
